import { Text, Pill } from "components/commons";
import { dateFormat, formatAmount, timeFormat } from "utils";
import styles from "./shift-records.module.scss";
import { locale } from "localization/en";
import { FormMode, Path, ShiftStatus } from "enums";
import { prettifyShiftStatus } from "utils/pretty.utils";
import classNames from "classnames";
import ValidatedPopOverMenu from "./shift-records-menu";

export const initializePumpTotals = ({ currentShift, pageMode }) => {
  let totals = [];

  const currentShiftPumpTotals = currentShift?.shiftPumpTotals;

  currentShiftPumpTotals.forEach((currentPump) => {
    if (currentPump.fuelingPointId === "Pump 02") {
      return;
    }

    Object.keys(currentPump).forEach((key) => {
      if (currentPump[key] < 0) {
        currentPump[key] = "0.000";
        return;
      }

      const lowerKey = key.toLowerCase();
      const isTotalizer =
        (lowerKey.includes("start") || lowerKey.includes("end")) && !lowerKey.includes("old");

      if (isTotalizer) {
        currentPump[key] =
          currentPump[key] === null
            ? ""
            : currentPump[key] === 0
              ? "0.000"
              : String(currentPump[key]);
      }
    });

    totals.push(currentPump);
  });

  totals = totals.map((pump) => {
    const keysToDelete = [];

    Object.keys(pump).forEach((key) => {
      if (key.toLowerCase().includes("start") && !key.toLowerCase().includes("old")) {
        const productCode = key.replace("startTotalizer", "").toLowerCase();
        const transactions = currentShift.transactionItem?.[`${productCode}Transactions`];

        const isProductAvailable =
          transactions?.length > 0
            ? true
            : isProductExistInUGT(productCode, currentShift.station.stationUgt);

        if (!isProductAvailable) {
          keysToDelete.push(key);
        }
      }

      if (key.toLowerCase().includes("end") && !key.toLowerCase().includes("old")) {
        const productCode = key.replace("endTotalizer", "").toLowerCase();
        const transactions = currentShift.transactionItem?.[`${productCode}Transactions`];

        const isProductAvailable =
          transactions?.length > 0
            ? true
            : isProductExistInUGT(productCode, currentShift.station.stationUgt);

        if (!isProductAvailable) {
          keysToDelete.push(key);
        }
      }
    });

    keysToDelete.forEach((key) => delete pump[key]);

    return pump;
  });

  return totals;
};

export const isProductExistInUGT = (productCode, stationUgt = []) => {
  return stationUgt.some((ugt) => ugt.productCode === productCode);
};

export const mapDataToList = ({
  shifts,
  history,
  showPrevModal,
  showSyncModal,
  showReopenModal,
}) => {
  const {
    startShift,
    endShift,
    shiftNumber,
    status,
    employees,
    totalBosTransactionCount,
    shiftId,
    totalTransactionCount,
    totalSales,
    totalAdjustments,
    isRecentClosedShift,
  } = shifts;

  const transactionCount = totalBosTransactionCount || 0;
  let syncPercentage = calculateSyncProgress({
    totalBosTransactionCount,
    totalTransactionCount,
  });

  let syncStatus = "pending";
  if (syncPercentage === 100) {
    syncStatus = "done";
  } else if (syncPercentage > 100 && syncPercentage < 0) {
    syncStatus = "ongoing";
  }

  let cashier = "--";
  if (employees && employees.length > 0) {
    const copyEmployee = [...employees];
    copyEmployee.sort((a, b) => {
      return b.shiftEmployeeId - a.shiftEmployeeId;
    });
    cashier = copyEmployee[employees.length - 1].name;
  }

  const options = [
    {
      content: locale.viewDetails,
      onClick: () => {
        history.push(Path.ViewShiftDetailsInfoById(shiftId, FormMode.View));
      },
    },
  ];
  if (status === ShiftStatus.Ended && syncPercentage === 100) {
    options.push({
      content: locale.editDetails,
      onClick: () => {
        history.push(Path.ViewShiftDetailsInfoById(shiftId, FormMode.Edit));
      },
    });
  }

  return {
    startShift: (
      <>
        <Text className={styles.td}>{dateFormat(startShift ?? "")}</Text>
        <Text className={styles.td}>{timeFormat(startShift ?? "")}</Text>
      </>
    ),
    endShift: (
      <>
        <Text className={styles.td}>{endShift != null ? dateFormat(endShift) : "--"}</Text>
        <Text className={styles.td}>{endShift != null ? timeFormat(endShift) : ""}</Text>
      </>
    ),
    shiftNumber: (
      <>
        <Text className={styles.td}>
          {status === ShiftStatus.Started ? locale.currentShift : (shiftNumber ?? "--")}
        </Text>
      </>
    ),
    cashier: (
      <>
        <Text className={styles.td}>{cashier}</Text>
      </>
    ),
    totalSales: (
      <>
        <Text className={styles.td}>{formatAmount(totalSales ?? 0)}</Text>
      </>
    ),
    adjustmentTotal: (
      <>
        <Text className={styles.td}>
          {formatAmount(
            totalAdjustments ?? 0,
            undefined,
            undefined,
            undefined,
            totalAdjustments < 0,
          )}
        </Text>
      </>
    ),
    shiftStatus: (
      <Pill
        className={styles.status}
        cheddar={status === ShiftStatus.Ended}
        grass={status === ShiftStatus.Started}
        cement={status === ShiftStatus.Closed}
      >
        {prettifyShiftStatus(status)}
      </Pill>
    ),
    syncProgress: (
      <div className={styles.sync}>
        <Text className={classNames(styles.percentage, styles[syncStatus])}>
          {status !== ShiftStatus.Started && `${syncPercentage} %`}
          {status === ShiftStatus.Started ? (
            <span className={styles.content}>{`(${transactionCount})`}</span>
          ) : (
            <span
              className={styles.content}
            >{`(${transactionCount}/${totalTransactionCount})`}</span>
          )}
        </Text>
      </div>
    ),
    actions: (
      <ValidatedPopOverMenu
        shifts={shifts}
        history={history}
        syncPercentage={syncPercentage}
        showPrevModal={showPrevModal}
        showSyncModal={showSyncModal}
        showReopenModal={showReopenModal}
        isRecentClosedShift={isRecentClosedShift}
      />
    ),
  };
};

export const mapFilterToRequest = (filterState) => {
  const { statuses, cashiers, ...fs } = filterState;
  let newCashiers;
  if (cashiers.value.length && !cashiers.isAll) {
    newCashiers = cashiers.value
      .map(({ value }) => {
        return value;
      })
      .join(",");
  } else {
    newCashiers = null;
  }

  return {
    ...fs,
    cashiers: newCashiers,
    statuses: !statuses.isSelectedAll ? statuses.value.join(",") : null,
  };
};

export const calculateSyncProgress = ({
  totalTransactionCount = 0,
  totalBosTransactionCount = 0,
}) => {
  if (Number(totalTransactionCount) === 0 && Number(totalBosTransactionCount) === 0) {
    return 100;
  }

  const syncPercentage =
    totalTransactionCount > 0 ? (totalBosTransactionCount / totalTransactionCount) * 100 : 0;

  return Math.round(syncPercentage * 100) / 100;
};
